import { useEffect, useRef } from 'react';
import { useIsTouch } from '../hooks/useMediaQuery';

/**
 * The gestures, named for the device you are holding. Telling someone on a
 * phone to "right-drag to rotate" or to "click" describes hardware they do not
 * have, and the pointer gestures are genuinely different -- two fingers, not a
 * second mouse button.
 */
const POINTER_GESTURES: ReadonlyArray<readonly [string, string]> = [
  ['Drag', 'Pan the map. Right-drag to rotate and tilt'],
  ['Zoom in', 'Every tree in the viewable area loads its LiDAR canopy'],
  ['Click tree', 'Isolate it. Click more to compare a few; clear to return to the whole view'],
  ['Click building', 'Measure the canopy against that building alone'],
  ['Sun arc', 'Scrub time of day; the band below sets the date'],
  ['Shade analysis', 'Switch it on to color the canopy by what each part of it shades'],
];

const TOUCH_GESTURES: ReadonlyArray<readonly [string, string]> = [
  ['Drag', 'Pan the map. Two fingers to rotate and tilt'],
  ['Pinch in', 'Every tree in the viewable area loads its LiDAR canopy'],
  ['Tap tree', 'See its details and isolate it. Tap more to compare a few'],
  ['Tap building', 'Measure the canopy against that building alone'],
  ['Sun arc', 'Drag to scrub time of day; the band below sets the date'],
  ['Pull up ▲', 'The bar above the sun opens the shade breakdown and settings'],
];

interface Props {
  onDismiss(): void;
}

export function WelcomeOverlay({ onDismiss }: Props) {
  const enterRef = useRef<HTMLButtonElement>(null);
  const gestures = useIsTouch() ? TOUCH_GESTURES : POINTER_GESTURES;

  /**
   * `autoFocus` on the button used to do this, and on a phone it opened the
   * dialog already scrolled: the card's body is a scroll container, the button
   * is near its end, and focusing an element scrolls it into view. The title
   * and the paragraph explaining what this is were above the fold, unread.
   * Focus still lands on the button -- it just does not drag the card with it.
   */
  useEffect(() => {
    enterRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div
      className="welcome"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div className="welcome__card">
        {/* On a phone the card fills the screen, so there is no backdrop left
            to tap to dismiss. */}
        <button
          type="button"
          className="welcome__close"
          onClick={onDismiss}
          aria-label="Close"
        >
          ×
        </button>

        <figure className="welcome__figure">
          <img src="/asset/Portrait_overheadview.jpg" alt="Overhead LiDAR view of New York City tree canopy" />
        </figure>

        <div className="welcome__body">
          <h1 className="welcome__title" id="welcome-title">
            Tree Folio <em>NYC</em>
          </h1>
          <p className="welcome__tagline">A digital twin of the urban canopy</p>

          <p className="welcome__lede">
            Tree Folio extracts three-dimensional models of every street tree in New York City
            from high-resolution LiDAR, simulates the light and shade around them, and
            quantifies the shade they actually provide — so that planting strategies can
            respond to local need and add up to real relief in a warming city.
          </p>

          {/* A definition list, so the gesture and what it does are actually
              paired -- and so each can be styled as what it is. */}
          <dl className="welcome__keys">
            {gestures.map(([gesture, effect]) => (
              <div className="welcome__key" key={gesture}>
                <dt>{gesture}</dt>
                <dd>{effect}</dd>
              </div>
            ))}
          </dl>

          <button type="button" className="welcome__enter" onClick={onDismiss} ref={enterRef}>
            Explore the canopy
          </button>

          <p className="welcome__credit">
            By the{' '}
            <a href="https://labs.aap.cornell.edu/daslab" target="_blank" rel="noreferrer">
              Design Across Scales Lab
            </a>{' '}
            at{' '}
            <a href="https://aap.cornell.edu/" target="_blank" rel="noreferrer">
              Cornell AAP
            </a>
            , with the{' '}
            <a href="https://urban.tech.cornell.edu/" target="_blank" rel="noreferrer">
              Urban Tech Hub
            </a>{' '}
            at the{' '}
            <a href="https://tech.cornell.edu/" target="_blank" rel="noreferrer">
              Jacobs Technion-Cornell Institute
            </a>
            . Built on the New York LiDAR Survey and the NYC Street Tree Census.
          </p>
        </div>
      </div>
    </div>
  );
}
